import { QUANTITY_OPTIONS, REGISTRATION_QUANTITY_OPTIONS } from '@/shared/config';
import type { QuantityOption } from '@/shared/config';
import {
  DocumentType,
  formatMatrixPrefix,
  type EntityType,
  type SearchFilter,
} from '@/shared/lib';

export const VALIDITY_HISTORY_DOCUMENT_SEARCH_URL = '/document-revisions/validity-history';
export const VALIDITY_HISTORY_REGISTRATION_SEARCH_URL = '/matrices/validity-history';
export const VALIDITY_HISTORY_DOCUMENT_EXPORT_URL = `${VALIDITY_HISTORY_DOCUMENT_SEARCH_URL}/export/excel`;
export const VALIDITY_HISTORY_REGISTRATION_EXPORT_URL = `${VALIDITY_HISTORY_REGISTRATION_SEARCH_URL}/export/excel`;

export const VALIDITY_HISTORY_DEFAULT_PAGE_SIZE = QUANTITY_OPTIONS[0].id;

export interface ValidityHistoryTabConfig {
  /**
   * Each tab has its own source: Documentos reads `document_revisions`, Registros reads `matrices`.
   * The two tables have their own id sequences, so the same number addresses a row in each of
   * them, and asking the wrong source returns the wrong item, not an empty list.
   */
  searchUrl: string;
  /** The Excel of each tab is built by the same source as its list. */
  exportUrl: string;
  emptyTitle: string;
  quantityOptions: QuantityOption[];
  entityName: EntityType;
  /** The records API returns the prefix as stored ("50.1"); the screen shows "RQ50.1". */
  formatPrefix: (prefix: string) => string;
}

export const VALIDITY_HISTORY_TAB_CONFIG: Record<DocumentType, ValidityHistoryTabConfig> = {
  [DocumentType.DOCUMENT]: {
    searchUrl: VALIDITY_HISTORY_DOCUMENT_SEARCH_URL,
    exportUrl: VALIDITY_HISTORY_DOCUMENT_EXPORT_URL,
    emptyTitle: 'Nenhum documento encontrado no histórico de vigências',
    quantityOptions: QUANTITY_OPTIONS,
    entityName: 'documento',
    formatPrefix: (prefix) => prefix,
  },
  [DocumentType.REGISTRATION]: {
    searchUrl: VALIDITY_HISTORY_REGISTRATION_SEARCH_URL,
    exportUrl: VALIDITY_HISTORY_REGISTRATION_EXPORT_URL,
    emptyTitle: 'Nenhum registro encontrado no histórico de vigências',
    quantityOptions: REGISTRATION_QUANTITY_OPTIONS,
    entityName: 'registro',
    formatPrefix: formatMatrixPrefix,
  },
};

export const getValidityHistoryTabConfig = (
  documentType: DocumentType
): ValidityHistoryTabConfig =>
  Object.prototype.hasOwnProperty.call(VALIDITY_HISTORY_TAB_CONFIG, documentType)
    ? VALIDITY_HISTORY_TAB_CONFIG[documentType]
    : VALIDITY_HISTORY_TAB_CONFIG[DocumentType.DOCUMENT];

export const getValidityHistorySearchUrl = (documentType: DocumentType): string =>
  getValidityHistoryTabConfig(documentType).searchUrl;

export const buildDefaultValidityHistoryFilters = (
  documentType: DocumentType = DocumentType.DOCUMENT
): SearchFilter => ({
  status: '',
  elaboratedBy: '',
  approvedBy: '',
  verifiedBy: '',
  elaborationDate: '',
  approvalDate: '',
  verificationDate: '',
  effectiveDate: '',
  revision: '',
  documentType,
});
